import { property, state } from "lit/decorators.js";
import { createRef, type Ref } from "lit/directives/ref.js";
import EkoElement from "./element";

export abstract class EkoChoiceElement extends EkoElement {
  static formAssociated = true;

  protected emitChange = () => {
    this.updateChecked();
    this.checkValidity();

    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { checked: this.checked, value: this.value },
      })
    );
  };

  protected handleBlur = () => {
    this.dispatchEvent(new FocusEvent("blur", { bubbles: true }));
    this.checkValidity();
  };

  protected handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.checked = input.checked;
    this.emitChange();
  };

  protected handleFocus = () => {
    this.dispatchEvent(new FocusEvent("focus", { bubbles: true }));
  };

  protected inputRef: Ref<HTMLInputElement> = createRef();

  protected internals: ElementInternals;

  protected slottedInput?: HTMLInputElement;

  protected validationMessage: string = "Please select this option if you want to proceed.";

  @property({ type: Boolean })
  binary: boolean = false;

  @property({ reflect: true, type: Boolean })
  checked: boolean = false;

  @property({ reflect: true, type: Boolean })
  disabled: boolean = false;

  @state()
  hasError: boolean = false;

  @property({ type: String })
  hint: string = "";

  @property({ type: String })
  label: string = "";

  @property({ type: String })
  name: string = "";

  @property({ type: Boolean })
  required: boolean = false;

  @property({ type: Boolean }) slotted = false;

  @property({ type: String })
  value: string = "";

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  checkValidity() {
    // Handle required validation
    if (this.required && !this.checked) {
      this.internals.setValidity({ valueMissing: true }, this.validationMessage, this.inputElement);
      this.hasError = true;
      return false;
    }

    // Clear validation state if valid
    this.internals.setValidity({});
    this.hasError = false;
    return this.internals.checkValidity();
  }

  focus() {
    if (this.inputElement) {
      this.inputElement.focus();
    }
  }

  // Form lifecycle methods
  formAssociatedCallback() {
    // Called when associated with a form
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    if (this.slotted) return;

    this.checked = false;
    this.internals.setFormValue(null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    if (this.slotted) return;

    if (state) {
      this.checked = true;
      this.internals.setFormValue(state);
    } else {
      this.checked = false;
      this.internals.setFormValue(null);
    }
  }

  reportValidity() {
    return this.internals.reportValidity();
  }

  updateChecked() {
    if (this.inputElement) {
      this.inputElement.checked = this.checked;
    }

    // Skip setFormValue when using a slotted input - the native input
    // already participates in form submission, so we'd get duplicate values
    if (this.slotted) return;

    // Update form value when checked state changes
    if (this.binary) {
      // For binary mode, use 0/1 values instead of true/false
      this.internals.setFormValue(this.checked ? this.value || "1" : "0");
    } else {
      // For non-binary mode, use the original behavior
      if (this.checked) {
        this.internals.setFormValue(this.value || "on");
      } else {
        this.internals.setFormValue(null);
      }
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("checked")) {
      this.updateChecked();
      this.checkValidity();
    }

    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";
      this.slottedInput?.toggleAttribute("disabled", this.disabled);
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }
  }

  get form() {
    return this.internals.form;
  }

  protected get inputElement(): HTMLInputElement | undefined {
    if (this.slottedInput) return this.slottedInput;
    const refValue = this.inputRef?.value;
    return refValue instanceof HTMLInputElement ? refValue : undefined;
  }

  get type(): string {
    return this.localName;
  }
}

export default EkoChoiceElement;
